import React from 'react'
import { BaseViewProps } from './types'
import { useTheme } from '@/hooks/useTheme'
import { responsiveValueAdapter, responsiveMarginValueAdapter } from '@/utils'

export const BaseView: React.FC<BaseViewProps> = ({
  bgColor,
  position = 'static',

  w,
  h,

  gap,
  flex,

  p,
  px,
  py,
  pt,
  pr,
  pb,
  pl,

  m,
  mx,
  my,
  mt,
  mr,
  mb,
  ml,

  rounded,
  roundedTopLeft,
  roundedTopRight,
  roundedBottomLeft,
  roundedBottomRight,

  fullWidth = false,
  circle = false,
  circleSize = 24,
  row = false,
  center = false,
  align,
  self,
  justify,
  style,
  children,
  className,
  'data-testid': dataTestId,
  'data-component': dataComponent,
  ...rest
}) => {
  const theme = useTheme()

  // Функция для конвертации spacing значений с responsive поддержкой
  const getSpacingValue = (
    value?: number | string,
    isVertical = false,
  ): string | undefined => {
    if (value === undefined) return undefined
    if (typeof value === 'string') return value
    const responsiveValue = responsiveValueAdapter(value, isVertical)
    return responsiveValue ? `${responsiveValue}px` : undefined
  }

  // Функция для margin значений
  const getMarginValue = (
    value?: number | string,
    isVertical = false,
  ): string | undefined => {
    if (value === undefined) return undefined
    if (typeof value === 'string') return value
    const responsiveValue = responsiveMarginValueAdapter(value, isVertical)
    return responsiveValue ? `${responsiveValue}px` : undefined
  }

  // Resolve background color from theme if it's a theme key
  const resolvedBgColor =
    bgColor && typeof bgColor === 'string' && bgColor.includes('.')
      ? getBgColorFromTheme(theme, bgColor)
      : bgColor

  // Helper function to get color from theme path
  function getBgColorFromTheme(
    theme: { colors: Record<string, any> },
    path: string,
  ): string {
    const keys = path.split('.')
    let value: any = theme.colors
    for (const key of keys) {
      value = value?.[key]
    }
    return (value as string) || bgColor || ''
  }

  // Стили, которые не могут быть заменены Tailwind классами
  const inlineStyles: React.CSSProperties = {
    // Background color (если задан)
    ...(resolvedBgColor && { backgroundColor: resolvedBgColor }),

    // Dimensions (если заданы конкретные значения)
    ...(w && !fullWidth && { width: getSpacingValue(w) }),
    ...(h && { height: circle ? `${circleSize}px` : getSpacingValue(h, true) }),

    // Circle dimensions override
    ...(circle && {
      width: `${circleSize}px`,
      height: `${circleSize}px`,
    }),

    // Flex
    ...(flex && { flex }),

    // Gap (если задан конкретное значение)
    ...(gap && { gap: getSpacingValue(gap, !row) }),

    // Padding (если заданы конкретные значения)
    ...(p && { padding: getSpacingValue(p) }),
    ...(px && {
      paddingLeft: getSpacingValue(px),
      paddingRight: getSpacingValue(px),
    }),
    ...(py && {
      paddingTop: getSpacingValue(py, true),
      paddingBottom: getSpacingValue(py, true),
    }),
    ...(pt && { paddingTop: getSpacingValue(pt, true) }),
    ...(pr && { paddingRight: getSpacingValue(pr) }),
    ...(pb && { paddingBottom: getSpacingValue(pb, true) }),
    ...(pl && { paddingLeft: getSpacingValue(pl) }),

    // Margin (если заданы конкретные значения)
    ...(m && { margin: getMarginValue(m) }),
    ...(mx && {
      marginLeft: getMarginValue(mx),
      marginRight: getMarginValue(mx),
    }),
    ...(my && {
      marginTop: getMarginValue(my, true),
      marginBottom: getMarginValue(my, true),
    }),
    ...(mt && { marginTop: getMarginValue(mt, true) }),
    ...(mr && { marginRight: getMarginValue(mr) }),
    ...(mb && { marginBottom: getMarginValue(mb, true) }),
    ...(ml && { marginLeft: getMarginValue(ml) }),

    // Border radius (если заданы конкретные значения)
    ...(rounded && !circle && { borderRadius: `${rounded}px` }),
    ...(roundedTopLeft && { borderTopLeftRadius: `${roundedTopLeft}px` }),
    ...(roundedTopRight && { borderTopRightRadius: `${roundedTopRight}px` }),
    ...(roundedBottomLeft && {
      borderBottomLeftRadius: `${roundedBottomLeft}px`,
    }),
    ...(roundedBottomRight && {
      borderBottomRightRadius: `${roundedBottomRight}px`,
    }),

    // Custom styles override
    ...style,
  }

  // Создаем className прямо здесь
  const baseClasses = 'flex'
  const positionClass =
    position === 'relative'
      ? 'relative'
      : position === 'absolute'
        ? 'absolute'
        : position === 'fixed'
          ? 'fixed'
          : position === 'sticky'
            ? 'sticky'
            : ''
  const directionClass = row ? 'flex-row' : 'flex-col'
  const alignmentClasses = center
    ? 'items-center justify-center'
    : `${
        align === 'center'
          ? 'items-center'
          : align === 'flex-start'
            ? 'items-start'
            : align === 'flex-end'
              ? 'items-end'
              : align === 'stretch'
                ? 'items-stretch'
                : ''
      } ${
        justify === 'center'
          ? 'justify-center'
          : justify === 'flex-start'
            ? 'justify-start'
            : justify === 'flex-end'
              ? 'justify-end'
              : justify === 'space-between'
                ? 'justify-between'
                : justify === 'space-around'
                  ? 'justify-around'
                  : justify === 'space-evenly'
                    ? 'justify-evenly'
                    : ''
      }`
  const selfClass =
    self === 'center'
      ? 'self-center'
      : self === 'start' || self === 'flex-start'
        ? 'self-start'
        : self === 'end' || self === 'flex-end'
          ? 'self-end'
          : self === 'stretch'
            ? 'self-stretch'
            : ''
  const widthClass = fullWidth ? 'w-full' : ''
  const circleClass = circle ? 'rounded-full' : ''

  const combinedClassName = [
    baseClasses,
    positionClass,
    directionClass,
    alignmentClasses,
    selfClass,
    widthClass,
    circleClass,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      style={inlineStyles}
      className={combinedClassName}
      data-testid={dataTestId}
      data-component={dataComponent}
      {...rest}
    >
      {children}
    </div>
  )
}
